import { FieldType, FormStepGroupType, FormStepType } from "@/data/forms/types";
import { isISODate, convertISOToUSDate } from "@/utils/dates";

type IsGroupVisibleFn = (params: {
  groupName: string;
  formValues: Record<string, any>;
  formSteps: FormStepType[];
}) => boolean;

export const isEmptyStep = ({
  step,
  formData,
  formSteps,
  isGroupVisible,
}: {
  step: FormStepType;
  formData: Record<string, any>;
  formSteps: FormStepType[];
  isGroupVisible: IsGroupVisibleFn;
}): boolean => {
  let isEmpty = true;
  step.groups
    .filter((group) => isGroupVisible({ groupName: group.name, formValues: formData, formSteps }))
    .forEach((group: any) => {
      group.fields.forEach((field: any) => {
        if (formData[field.name]) isEmpty = false;
      });
    });

  return isEmpty;
};

const translate = (key: string | undefined, t: Record<string, string>) => (key && t[key]) || key;

const fieldsToHideInReview = [
  "emailAddressValidation",
  "removeAdditionalPhoneNumber1",
  "removeAdditionalPhoneNumber2",
  "hasChangedName",
  "firstNameIncorrectCheck",
  "lastNameIncorrectCheck",
  "dateOfBirthIncorrectCheck",
  "placeOfBirthIncorrectCheck",
  "genderIncorrectCheck",
  "addons",
];

export const getFieldData = ({
  field,
  formData,
  t,
}: {
  field: FieldType | { check: FieldType; content: FieldType };
  formData: Record<string, any>;
  t: Record<string, string>;
}): { value: any; label: string; isVisible: boolean } => {
  //
  const isPairedField = typeof field === "object" && "check" in field && "content" in field;

  let fieldValue: any;
  let fieldLabel: string;
  let fieldIsVisible: boolean = false;
  let component: string | undefined;
  let options: any[] | undefined;

  // Paired Fields
  if (isPairedField) {
    const { check, content } = field as {
      check: FieldType;
      content: FieldType;
    };

    fieldValue = formData[content.name];
    fieldLabel = t[check.reviewLabel!] || t[check.label];
    fieldIsVisible = !!formData[check.name];
    component = content.component;
    options = content.options;
  }

  // Normal Fields
  else {
    const f = field as FieldType;
    fieldValue = formData[f.name];
    fieldLabel = t[f.reviewLabel!] || t[f.label];
    component = f.component;
    options = f.options;

    const isRelativeUnknown = f.name === "relative1Unknown" || f.name === "relative2Unknown";
    if (isRelativeUnknown) {
      const relativeNumber = f.name === "relative1Unknown" ? "1" : "2";
      fieldLabel = `${t["Relative"]} ${relativeNumber}`;

      if (fieldValue) {
        fieldValue = t["Unknown"];
      } else {
        fieldIsVisible = true;
      }
    }
  }

  // Common Logics
  if (isISODate(fieldValue)) {
    fieldValue = convertISOToUSDate(fieldValue);
  }

  if (["Select", "Radio"].includes(component || "")) {
    const option = options?.find((opt) => opt.value === fieldValue);
    if (option) {
      fieldValue = translate(option.label, t) || translate(option.name, t);
    }
  }

  const hasValue = !!fieldValue;
  const isFieldToHide = fieldsToHideInReview.includes((field as FieldType).name);

  return {
    value: fieldValue,
    label: fieldLabel,
    isVisible: isFieldToHide ? false : fieldIsVisible || hasValue,
  };
};

export const getIncorrectPrintedPairedField = ({
  field,
  group,
}: {
  field: FieldType;
  group: FormStepGroupType;
}): { check: FieldType; content: FieldType } => {
  const nameCheck = field.name;
  const nameContent = nameCheck.replace("Check", "Print");

  const contentField = group.fields.find((f) => f.name === nameContent);
  if (!contentField) {
    throw new Error(`We couldn't find the ${nameContent} field for ${nameCheck}`);
  }
  return {
    check: { ...field },
    content: { ...contentField },
  };
};
